import type { Persona } from "./types";

/**
 * Map a first-touch referring domain (or utm_source token) to a likely persona.
 * Patterns are matched as substrings against the referrer hostname and utm_source,
 * so "crates.io" matches "crates.io" and a utm_source of "crates" alike.
 */
const DOMAIN_MAP: { persona: Persona; patterns: string[] }[] = [
  {
    persona: "writer",
    patterns: [
      "querytracker",
      "submittable",
      "duotrope",
      "manuscriptwishlist",
      "absolutewrite",
      "nanowrimo",
      "scribophile",
      "wattpad",
      "reedsy",
      "chillsubs",
      "writersdigest",
      "poetsandwriters",
      "the-efa",
      "authorspublish",
    ],
  },
  {
    persona: "publishing",
    patterns: [
      "publishersmarketplace",
      "publishersweekly",
      "publishlyawards",
      "wiley",
      "penguinrandomhouse",
      "harpercollins",
      "macmillan",
      "hachette",
      "simonandschuster",
      "springer",
      "elsevier",
      "sagepub",
      "taylorfrancis",
      "thebookseller",
      "bookscout",
      "ingramcontent",
      "netgalley",
    ],
  },
  {
    persona: "creator",
    patterns: [
      "medium.com",
      "quora.com",
      "substack",
      "wordpress",
      "ghost.org",
      "blogger",
      "tumblr",
      "patreon",
      "youtube",
      "tiktok",
      "contently",
    ],
  },
  {
    persona: "developer",
    patterns: [
      "github",
      "gitlab",
      "bitbucket",
      "stackoverflow",
      "stackexchange",
      "pypi.org",
      "npmjs",
      "crates.io",
      "docs.rs",
      "readthedocs",
      "news.ycombinator",
      "dev.to",
      "huggingface",
      "lobste.rs",
    ],
  },
  {
    persona: "standards",
    patterns: [
      "c2pa.org",
      "contentauthenticity",
      "dif.foundation",
      "identity.foundation",
      "cawg",
      "trustoverip",
      "linuxfoundation",
      "lfdecentralizedtrust",
      "unicode.org",
      "ietf.org",
      "datatracker",
      "w3.org",
      "iptc.org",
      "iso.org",
      "nist.gov",
      "oasis-open",
    ],
  },
];

// Referrers that carry no useful persona signal (own site, search, generic social).
const IGNORED = [
  "writerslogic",
  "writersproof",
  "google.",
  "bing.",
  "duckduckgo",
  "yahoo.",
  "ecosia",
  "t.co",
  "facebook",
  "instagram",
  "reddit",
];

function hostFrom(referrer: string): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function utmSourceFrom(search: string): string {
  try {
    return (new URLSearchParams(search).get("utm_source") || "").toLowerCase();
  } catch {
    return "";
  }
}

export interface ReferrerHit {
  persona: Persona;
  matched: string;
}

/**
 * Classify a visit's referrer + utm_source into a persona, or null when there's no
 * meaningful signal (direct, search, own domain, generic social).
 */
export function classifyReferrer(
  referrer: string,
  search: string,
): ReferrerHit | null {
  const host = hostFrom(referrer);
  const utm = utmSourceFrom(search);
  const haystacks = [host, utm].filter(Boolean);
  if (haystacks.length === 0) return null;

  if (host && IGNORED.some((i) => host.includes(i))) {
    // Own-site / search / generic social referrers only count if utm overrides.
    if (!utm) return null;
  }

  for (const hay of haystacks) {
    for (const { persona, patterns } of DOMAIN_MAP) {
      const matched = patterns.find((p) => hay.includes(p));
      if (matched) return { persona, matched };
    }
  }
  return null;
}
